import { createTheme } from "@aws-amplify/ui-react";

/**
 * Evido brand palette.
 */
const EVIDO_GREEN = "#1A4133";
const EVIDO_GREEN_HOVER = "#143328";
const EVIDO_GREEN_ACTIVE = "#10281F";
const EVIDO_GREEN_TINT_LIGHTEST = "#E9F0ED";
const EVIDO_GREEN_TINT_LIGHT = "#C6D8D1";
const EVIDO_GREEN_TINT_MEDIUM = "#8FB3A6";
const EVIDO_GREEN_TINT_STRONG = "#4A7A66";

const BRIGHT_GREEN = "#96E6CA";
const BRIGHT_GREEN_HOVER = "#7FDDBD";
const BRIGHT_GREEN_ACTIVE = "#68D3AF";

const HIGHLIGHT_BLUE = "#E7F6FF";

const PAGE_BACKGROUND_GREY = "#E2E2E2";
const CARD_BACKGROUND = "#FFFFFF";
const INPUT_OUTLINE_GREY = "#E2E2E2";
const INPUT_TEXT_GREY = "#595959";

const BARLOW_FONT_STACK = '"Barlow", system-ui, -apple-system, sans-serif';

/** Radius the brand guide specifies for the primary (pill-shaped) button. */
const PILL_BUTTON_RADIUS = "23px";
/** Radius the brand guide specifies for text inputs. */
const INPUT_RADIUS = "2px";
/** Radius the brand guide specifies for cards and content panels. */
const PANEL_RADIUS = "16px";

/**
 * Evido theme for the Amplify UI components.
 */
export const evidoTheme = createTheme({
  name: "evido",
  tokens: {
    fonts: {
      default: {
        variable: { value: BARLOW_FONT_STACK },
        static: { value: BARLOW_FONT_STACK },
      },
    },
    radii: {
      large: { value: PANEL_RADIUS },
    },
    colors: {
      primary: {
        10: { value: EVIDO_GREEN_TINT_LIGHTEST },
        20: { value: EVIDO_GREEN_TINT_LIGHT },
        40: { value: EVIDO_GREEN_TINT_MEDIUM },
        60: { value: EVIDO_GREEN_TINT_STRONG },
        80: { value: EVIDO_GREEN },
        90: { value: EVIDO_GREEN_HOVER },
        100: { value: EVIDO_GREEN_ACTIVE },
      },
      secondary: {
        40: { value: BRIGHT_GREEN },
        60: { value: BRIGHT_GREEN_HOVER },
        80: { value: BRIGHT_GREEN_ACTIVE },
      },
      background: {
        secondary: { value: PAGE_BACKGROUND_GREY },
        info: { value: HIGHLIGHT_BLUE },
      },
      font: {
        primary: { value: EVIDO_GREEN },
        secondary: { value: INPUT_TEXT_GREY },
      },
      border: {
        primary: { value: INPUT_OUTLINE_GREY },
      },
    },
    components: {
      authenticator: {
        router: {
          borderWidth: { value: "0" },
          backgroundColor: { value: CARD_BACKGROUND },
          boxShadow: { value: "0 8px 28px rgba(26, 65, 51, 0.10)" },
        },
        form: { padding: { value: "2.5rem" } },
      },
      heading: {
        color: { value: EVIDO_GREEN },
        1: { fontSize: { value: "22px" }, fontWeight: { value: "500" } },
        2: { fontSize: { value: "16px" }, fontWeight: { value: "500" } },
        3: { fontSize: { value: "24px" }, fontWeight: { value: "600" } },
      },
      fieldcontrol: {
        borderColor: { value: INPUT_OUTLINE_GREY },
        borderRadius: { value: INPUT_RADIUS },
        color: { value: INPUT_TEXT_GREY },
        fontSize: { value: "14px" },
        paddingBlockStart: { value: "0.8rem" },
        paddingBlockEnd: { value: "0.8rem" },
        paddingInlineStart: { value: "1rem" },
        paddingInlineEnd: { value: "1rem" },
        _focus: {
          borderColor: { value: EVIDO_GREEN },
          boxShadow: { value: `0 0 0 1px ${EVIDO_GREEN}` },
        },
      },
      field: {
        label: { color: { value: EVIDO_GREEN } },
      },
      searchfield: {
        button: {
          backgroundColor: { value: "transparent" },
          color: { value: INPUT_TEXT_GREY },
          _hover: {
            backgroundColor: { value: "transparent" },
            color: { value: EVIDO_GREEN },
          },
          _focus: {
            backgroundColor: { value: "transparent" },
            color: { value: EVIDO_GREEN },
          },
          _active: {
            backgroundColor: { value: "transparent" },
            color: { value: EVIDO_GREEN },
          },
        },
      },
      button: {
        borderRadius: { value: PILL_BUTTON_RADIUS },
        fontSize: { value: "16px" },
        fontWeight: { value: "400" },
        paddingBlockStart: { value: "1rem" },
        paddingBlockEnd: { value: "1rem" },
        small: {
          fontSize: { value: "12px" },
          paddingBlockStart: { value: "0.375rem" },
          paddingBlockEnd: { value: "0.375rem" },
          paddingInlineStart: { value: "1.25rem" },
          paddingInlineEnd: { value: "1.25rem" },
        },
        primary: {
          backgroundColor: { value: EVIDO_GREEN },
          color: { value: CARD_BACKGROUND },
          _hover: {
            backgroundColor: { value: EVIDO_GREEN_HOVER },
            color: { value: CARD_BACKGROUND },
          },
          _focus: {
            backgroundColor: { value: EVIDO_GREEN_HOVER },
            color: { value: CARD_BACKGROUND },
          },
          _active: {
            backgroundColor: { value: EVIDO_GREEN_ACTIVE },
            color: { value: CARD_BACKGROUND },
          },
        },
        link: {
          color: { value: EVIDO_GREEN },
          _hover: {
            backgroundColor: { value: "transparent" },
            color: { value: EVIDO_GREEN_HOVER },
          },
          _focus: {
            backgroundColor: { value: "transparent" },
            color: { value: EVIDO_GREEN_HOVER },
          },
          _active: {
            backgroundColor: { value: "transparent" },
            color: { value: EVIDO_GREEN_ACTIVE },
          },
        },
      },
      table: {
        header: {
          color: { value: CARD_BACKGROUND },
          fontSize: { value: "12px" },
          fontWeight: { value: "600" },
          padding: { value: "1rem" },
          borderWidth: { value: "0" },
        },
        data: {
          color: { value: EVIDO_GREEN },
          fontSize: { value: "14px" },
          borderColor: { value: INPUT_OUTLINE_GREY },
          padding: { value: "0.75rem 1rem" },
        },
        row: {
          hover: { backgroundColor: { value: HIGHLIGHT_BLUE } },
        },
      },
      divider: {
        borderColor: { value: INPUT_OUTLINE_GREY },
        label: {
          color: { value: INPUT_TEXT_GREY },
          fontSize: { value: "14px" },
          backgroundColor: { value: CARD_BACKGROUND },
        },
      },
      passwordfield: {
        button: {
          color: { value: INPUT_TEXT_GREY },
          _hover: {
            color: { value: EVIDO_GREEN },
            backgroundColor: { value: "transparent" },
          },
          _focus: {
            color: { value: EVIDO_GREEN },
            backgroundColor: { value: "transparent" },
          },
          _active: {
            color: { value: EVIDO_GREEN },
            backgroundColor: { value: "transparent" },
          },
        },
      },
    },
  },
});
